import { Button, toast } from '@sitewise/ui';
import { Mic, Square, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { structureTranscript, transcribeVoice } from '../api/ai.js';
import { presignUpload } from '../api/uploads.js';
import { ApiError } from '../lib/api-client.js';

import type { UploadKind } from '@sitewise/shared';

export interface VoiceRecorderResult {
  transcript: string;
  uploadId: string;
  audioSeconds: number;
  draft?: {
    title?: string;
    description?: string;
    severity?: 'low' | 'medium' | 'high' | 'critical';
    category?: string;
    confidence: number;
    buildingId?: string | null;
    unitId?: string | null;
    locationConfidence?: number;
  };
}

interface VoiceRecorderProps {
  /** Building the WO will be filed against — passed through to the
   *  structurer so it can scope unit hints. May be omitted while the
   *  user hasn't picked a building yet (we'll still transcribe). */
  buildingId?: string | null;
  /** Whether to also call /ai/voice/structure after transcription.
   *  Default true; set false when the caller just wants raw text. */
  autoStructure?: boolean;
  /** Upload kind for the audio blob. Defaults to work-order voice memos. */
  uploadKind?: Extract<UploadKind, 'work_order_voice_memo' | 'reminder_voice_memo'>;
  /** Fired when transcription (and optional structuring) finishes. */
  onComplete?: (result: VoiceRecorderResult) => void;
  /** Fired any time the user starts a fresh recording, so the caller
   *  can clear stale draft fields. */
  onReset?: () => void;
}

type RecorderState = 'idle' | 'recording' | 'uploading' | 'transcribing' | 'structuring' | 'done' | 'error';

const MAX_DURATION_SECONDS = 180;

/**
 * Voice memo recorder used inside the WO create sheet. Records audio
 * via MediaRecorder, uploads it as a `work_order_voice_memo`, then
 * calls `/ai/voice/transcribe` for the text and (optionally)
 * `/ai/voice/structure` for the suggested fields.
 */
export function VoiceRecorder({
  buildingId,
  autoStructure = true,
  uploadKind = 'work_order_voice_memo',
  onComplete,
  onReset,
}: VoiceRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [transcript, setTranscript] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [confidence, setConfidence] = useState<number | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const tickRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function startRecording() {
    setError(null);
    setTranscript('');
    setSeconds(0);
    setConfidence(null);
    onReset?.();

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Microphone access is not supported in this browser.');
      setState('error');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        chunksRef.current = [];
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (tickRef.current !== null) {
          window.clearInterval(tickRef.current);
          tickRef.current = null;
        }
        void uploadAndTranscribe(blob);
      };
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.start();
      setState('recording');
      tickRef.current = window.setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (next >= MAX_DURATION_SECONDS) {
            recorder.stop();
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start recording');
      setState('error');
    }
  }

  function stopRecording() {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }

  async function uploadAndTranscribe(blob: Blob) {
    setState('uploading');
    try {
      if (blob.size === 0) {
        setError('No audio was captured — try recording again and speak for at least a second.');
        setState('error');
        return;
      }
      const file = new File([blob], `voice-memo-${Date.now()}.webm`, {
        type: blob.type || 'audio/webm',
      });
      const presigned = await presignUpload({
        kind: uploadKind,
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.headers,
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed: HTTP ${putRes.status}`);
      }

      setState('transcribing');
      const tr = await transcribeVoice({ uploadId: presigned.uploadId });
      if (tr.status === 'queued') {
        setError(
          tr.message ??
            'Memo too long for instant transcription — we will keep the audio attached and process it in the background.',
        );
        setState('error');
        return;
      }
      if (tr.status === 'error') {
        setError(tr.message ?? `Transcription failed (${tr.reason ?? 'unknown'}).`);
        setState('error');
        return;
      }
      const text = tr.transcript ?? '';
      setTranscript(text);

      if (!autoStructure || !text.trim()) {
        setState('done');
        onComplete?.({
          transcript: text,
          uploadId: presigned.uploadId,
          audioSeconds: tr.audioSeconds ?? seconds,
        });
        return;
      }

      setState('structuring');
      const structured = await structureTranscript({
        transcript: text,
        ...(buildingId ? { buildingId } : {}),
      });
      const draft = structured.status === 'structured' ? structured.draft : undefined;
      if (draft) setConfidence(draft.confidence);
      setState('done');
      onComplete?.({
        transcript: text,
        uploadId: presigned.uploadId,
        audioSeconds: tr.audioSeconds ?? seconds,
        ...(draft
          ? {
              draft: {
                ...(draft.title ? { title: draft.title } : {}),
                ...(draft.description ? { description: draft.description } : {}),
                ...(draft.suggestedSeverity
                  ? { severity: draft.suggestedSeverity }
                  : {}),
                ...(draft.suggestedCategory
                  ? { category: draft.suggestedCategory }
                  : {}),
                confidence: draft.confidence,
                buildingId: draft.buildingId ?? null,
                unitId: draft.unitId ?? null,
                ...(typeof draft.locationConfidence === 'number'
                  ? { locationConfidence: draft.locationConfidence }
                  : {}),
              },
            }
          : {}),
      });
    } catch (err) {
      setError(describeError(err));
      toast.error('Voice memo failed');
      setState('error');
    }
  }

  function reset() {
    setState('idle');
    setTranscript('');
    setError(null);
    setSeconds(0);
    setConfidence(null);
    onReset?.();
  }

  if (state === 'idle' || state === 'error') {
    return (
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={startRecording}
        >
          <Mic className="h-3.5 w-3.5" />
          {state === 'error' ? 'Try again' : 'Record voice memo'}
        </Button>
        {error ? <span className="text-xs text-danger">{error}</span> : null}
      </div>
    );
  }

  if (state === 'recording') {
    return (
      <div className="flex items-center gap-2">
        <Button type="button" variant="destructive" size="sm" onClick={stopRecording}>
          <Square className="h-3.5 w-3.5" />
          Stop ({formatTime(seconds)})
        </Button>
        <span className="text-xs text-fg-muted">
          Speak clearly. Cuts off at {Math.floor(MAX_DURATION_SECONDS / 60)} min.
        </span>
      </div>
    );
  }

  if (state === 'uploading' || state === 'transcribing' || state === 'structuring') {
    const label =
      state === 'uploading'
        ? 'Uploading…'
        : state === 'transcribing'
          ? 'Transcribing with Whisper…'
          : 'Structuring fields with Claude…';
    return (
      <div className="text-xs text-fg-muted">
        <span className="inline-block animate-pulse">{label}</span>
      </div>
    );
  }

  const lowConfidence = confidence !== null && confidence < LOW_CONFIDENCE_THRESHOLD;

  return (
    <div className="space-y-2 rounded border border-border bg-surface-muted p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium">Transcript</p>
        <div className="flex items-center gap-2">
          {confidence !== null ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                lowConfidence
                  ? 'bg-warning-subtle text-warning'
                  : 'bg-success-subtle text-success'
              }`}
              title="How confident the AI is in the fields it filled in"
            >
              AI confidence {Math.round(confidence * 100)}%
            </span>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={reset}
            aria-label="Discard memo"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <p className="whitespace-pre-wrap text-xs italic text-fg-secondary">{transcript}</p>
      {lowConfidence ? (
        <p className="text-[11px] text-warning">
          Low confidence — please review the auto-filled title, description, severity, category,
          building, and unit before submitting.
        </p>
      ) : null}
    </div>
  );
}

/** Below this the auto-filled fields warrant a manual review nudge. Mirrors
 *  the inbound-email review threshold used elsewhere in the AI layer. */
const LOW_CONFIDENCE_THRESHOLD = 0.6;

/**
 * Turns an error into a user-readable string. For API validation errors the
 * generic top-level message is "Invalid request"; the useful information lives
 * in `details.fieldErrors`, so we pull the first field error out when present.
 */
function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const fieldErrors = (err.details as { fieldErrors?: Record<string, string[]> } | undefined)
      ?.fieldErrors;
    const firstFieldError = fieldErrors
      ? Object.values(fieldErrors).flat().find(Boolean)
      : undefined;
    return firstFieldError ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Voice memo failed';
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const candidate of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported?.(candidate)) return candidate;
  }
  return undefined;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}
